<template>
  <div class="vanilla-b-wrap">
    <main class="screen" role="main">
      <div class="header">
        <button
          type="button"
          class="back-button"
          @click="goBack"
          :disabled="!canGoBack"
          aria-label="Zurück"
        >←</button>
        <span class="section-counter">
          Abschnitt {{ currentSectionIndex + 1 }} von {{ sectionsWithQuestions.length }}
        </span>
      </div>

      <div
        class="progress-bar"
        role="progressbar"
        :aria-valuenow="currentSectionIndex + 1"
        aria-valuemin="1"
        :aria-valuemax="sectionsWithQuestions.length"
        aria-label="Fortschritt"
      >
        <div
          v-for="seg in progressSegments"
          :key="seg.sectionId"
          class="progress-segment"
          :style="{ flex: seg.size }"
        >
          <div
            v-if="seg.fill > 0"
            class="progress-segment-fill"
            :style="{ width: (seg.fill * 100) + '%' }"
          ></div>
        </div>
      </div>

      <div class="section-header">
        <h1 class="section-title">{{ currentSection.label }}</h1>
        <p class="section-prompt">{{ currentSection.prompt }}</p>
        <p class="section-progress-text">
          {{ answeredInSection }} von {{ currentSection.questions.length }} beantwortet
        </p>
      </div>

      <div class="question-list">
        <div
          v-for="q in currentSection.questions"
          :key="q.id"
          class="question-row"
        >
          <div class="question-row-header">
            <p class="question-row-title">{{ q.title }}</p>
            <button
              v-if="q.tooltip"
              type="button"
              class="tooltip-button-small"
              :aria-expanded="String(!!tooltipsOpen[q.id])"
              :aria-controls="`tt-${q.id}`"
              @click="toggleTooltip(q.id)"
            >ⓘ</button>
          </div>
          <div
            v-if="q.tooltip && tooltipsOpen[q.id]"
            :id="`tt-${q.id}`"
            class="tooltip-content"
          >
            {{ q.tooltip }}
          </div>
          <div class="answer-chips" role="radiogroup" :aria-label="q.title">
            <label
              v-for="opt in q.options"
              :key="opt.value"
              class="chip"
            >
              <input
                type="radio"
                :name="`q-${q.id}`"
                :value="opt.value"
                v-model="answers[q.id]"
              >
              <span class="chip-label">{{ opt.compactLabel || opt.label }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="footer">
        <button
          type="button"
          class="next-button"
          :disabled="!isComplete"
          @click="goNext"
        >{{ nextButtonLabel }}</button>
      </div>
    </main>
  </div>
</template>

<script>
import { sections, questions } from '../_data/sampleQuestions.js';

export default {
  name: 'VanillaB',
  data() {
    return {
      currentSectionIndex: 0,
      tooltipsOpen: {},
      answers: {},
    };
  },
  computed: {
    sectionsWithQuestions() {
      return sections
        .map(s => ({ ...s, questions: questions.filter(q => q.sectionId === s.id) }))
        .filter(s => s.questions.length > 0);
    },
    currentSection() {
      return this.sectionsWithQuestions[this.currentSectionIndex];
    },
    answeredInSection() {
      return this.currentSection.questions.filter(
        q => this.answers[q.id] !== undefined,
      ).length;
    },
    isComplete() {
      return this.answeredInSection === this.currentSection.questions.length;
    },
    isLastSection() {
      return this.currentSectionIndex === this.sectionsWithQuestions.length - 1;
    },
    canGoBack() {
      return this.currentSectionIndex > 0;
    },
    nextButtonLabel() {
      if (this.isLastSection) return 'Fertig';
      const nextSection = this.sectionsWithQuestions[this.currentSectionIndex + 1];
      return `Weiter zu ${nextSection.label}`;
    },
    progressSegments() {
      return this.sectionsWithQuestions.map((s, i) => {
        let fill = 0;
        if (i < this.currentSectionIndex) {
          fill = 1;
        } else if (i === this.currentSectionIndex && s.questions.length > 0) {
          fill = this.answeredInSection / s.questions.length;
        }
        return { sectionId: s.id, size: s.questions.length, fill };
      });
    },
  },
  methods: {
    toggleTooltip(qid) {
      this.tooltipsOpen = { ...this.tooltipsOpen, [qid]: !this.tooltipsOpen[qid] };
    },
    goBack() {
      if (!this.canGoBack) return;
      this.currentSectionIndex--;
      this.tooltipsOpen = {};
      this.scrollToTop();
    },
    goNext() {
      if (!this.isComplete) return;
      if (this.isLastSection) {
        this.$router.push('/postsurvey/email');
        return;
      }
      this.currentSectionIndex++;
      this.tooltipsOpen = {};
      this.scrollToTop();
    },
    scrollToTop() {
      this.$nextTick(() => {
        if (this.$el && typeof this.$el.scrollIntoView === 'function') {
          this.$el.scrollIntoView({ block: 'start' });
        }
      });
    },
  },
};
</script>

<style scoped>
.vanilla-b-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-progress-fill: #534AB7;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.vanilla-b-wrap *,
.vanilla-b-wrap *::before,
.vanilla-b-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1rem 1.25rem 0;
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 20px;
  color: var(--color-text-secondary);
  line-height: 1;
}

.back-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.section-counter {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 1.5rem;
}

.progress-segment {
  height: 6px;
  border-radius: 3px;
  background: var(--color-bg-muted);
  overflow: hidden;
}

.progress-segment-fill {
  height: 100%;
  background: var(--color-progress-fill);
  border-radius: 3px;
  transition: width 0.25s ease;
}

.section-header {
  margin-bottom: 1rem;
}

.section-title {
  font-size: 22px;
  font-weight: 500;
  margin: 0 0 6px;
  line-height: 1.3;
}

.section-prompt {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 4px;
}

.section-progress-text {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin: 0;
}

.question-list {
  flex: 1;
  margin: 0 -1.25rem;
  padding: 0 1.25rem;
}

.question-row {
  padding: 1rem 0;
  border-bottom: 0.5px solid var(--color-border);
}

.question-row:last-child {
  border-bottom: none;
}

.question-row-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.question-row-title {
  font-size: 15px;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
}

.tooltip-button-small {
  background: transparent;
  border: none;
  padding: 2px 6px;
  font-size: 14px;
  color: var(--color-text-tertiary);
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
}

.tooltip-button-small:hover {
  color: var(--color-text-secondary);
}

.tooltip-content {
  background: var(--color-bg-muted);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: 10px;
  line-height: 1.5;
}

.answer-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.chip {
  flex: 1;
  min-width: 0;
  position: relative;
}

.chip input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.chip-label {
  display: block;
  text-align: center;
  padding: 10px 6px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 12px;
  color: var(--color-text-secondary);
  cursor: pointer;
  background: var(--color-bg-card);
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  line-height: 1.3;
}

.chip-label:hover {
  background: var(--color-bg-muted);
}

.chip input:checked + .chip-label {
  border-color: var(--color-progress-fill);
  background: var(--color-bg-muted);
  color: var(--color-progress-fill);
  font-weight: 500;
}

.footer {
  padding: 1rem 1.25rem 1.25rem;
  background: var(--color-bg-card);
  border-top: 0.5px solid var(--color-border);
  margin: 0 -1.25rem;
  position: sticky;
  bottom: 0;
}

.next-button {
  width: 100%;
  padding: 14px;
  background: var(--color-bg-muted);
  color: var(--color-text-tertiary);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: not-allowed;
  transition: background 0.15s;
}

.next-button:not(:disabled) {
  background: var(--color-progress-fill);
  color: white;
  border-color: var(--color-progress-fill);
  cursor: pointer;
}

.next-button:not(:disabled):hover {
  opacity: 0.9;
}
</style>
